import type { SelectionItemRef } from '@puppycad/cad-core';
import type { BlendSelectionDisplayItem } from './types';

/**
 * 构建选择项显示标签。
 *
 * @param kind 选择项类型。
 * @param index 同类型序号。
 * @returns 显示标签。
 */
function buildSelectionLabel(kind: BlendSelectionDisplayItem['kind'], index: number): string {
  switch (kind) {
    case 'edge':
      return `边线<${index}>`;
    case 'face':
      return `面<${index}>`;
    case 'vertex':
      return `顶点<${index}>`;
    default:
      return `项目<${index}>`;
  }
}

/**
 * 从已有显示标签中读取同类型序号。
 *
 * @param kind 选择项类型。
 * @param label 显示标签。
 * @returns 序号；不是标准标签时返回 null。
 */
function parseSelectionLabelIndex(kind: BlendSelectionDisplayItem['kind'], label: string): number | null {
  const prefix = kind === 'edge'
    ? '边线'
    : kind === 'face'
      ? '面'
      : kind === 'vertex'
        ? '顶点'
        : '项目';
  const match = new RegExp(`^${prefix}<([1-9]\\d*)>$`).exec(label);
  if (!match) {
    return null;
  }
  const index = Number.parseInt(match[1]!, 10);
  return Number.isFinite(index) ? index : null;
}

/**
 * 读取当前显示项中某类型已使用的最大序号。
 *
 * @param items 当前显示项。
 * @param kind 选择项类型。
 * @returns 最大序号。
 */
function resolveMaxLabelIndexByKind(
  items: BlendSelectionDisplayItem[],
  kind: BlendSelectionDisplayItem['kind'],
): number {
  return items.reduce((maxIndex, item) => {
    if (item.kind !== kind) {
      return maxIndex;
    }
    const index = parseSelectionLabelIndex(kind, item.label);
    return typeof index === 'number' ? Math.max(maxIndex, index) : maxIndex;
  }, 0);
}

/**
 * 基于持久化选择项构建显示项。
 *
 * @param itemRef 选择项引用。
 * @returns 显示项。
 */
export function buildDisplayItemFromSelectionRef(itemRef: SelectionItemRef): BlendSelectionDisplayItem {
  return {
    key: `${itemRef.kind}:${itemRef.persistentRef}`,
    label: '',
    kind: itemRef.kind,
    objectId: itemRef.bodyObjectId,
    bodyObjectId: itemRef.bodyObjectId,
    persistentRef: itemRef.persistentRef,
    adjacentFaceRefs: itemRef.adjacentFaceRefs ? [...itemRef.adjacentFaceRefs] : undefined,
    runtimeTopologyHint: itemRef.runtimeTopologyHint
      ? {
        ...itemRef.runtimeTopologyHint,
        adjacentFaceRefs: [...(itemRef.runtimeTopologyHint.adjacentFaceRefs ?? itemRef.adjacentFaceRefs ?? [])],
        adjacentFaceHints: (itemRef.runtimeTopologyHint.adjacentFaceHints ?? []).map((faceHint) => ({ ...faceHint })),
      }
      : undefined,
  };
}

/**
 * 用持久化引用列表刷新显示项，并尽量复用已有的拓扑索引与标签信息。
 *
 * @param existing 现有显示项。
 * @param itemRefs 最新选择项。
 * @returns 刷新后的显示项。
 */
export function mergeDisplayItemsFromSelectionRefs(
  existing: BlendSelectionDisplayItem[],
  itemRefs: SelectionItemRef[],
): BlendSelectionDisplayItem[] {
  const existingByRef = new Map(existing.map((item) => [item.persistentRef, item]));
  const nextIndexByKind = new Map<BlendSelectionDisplayItem['kind'], number>();
  return itemRefs.map((itemRef) => {
    const existingItem = existingByRef.get(itemRef.persistentRef);
    if (existingItem?.label) {
      return existingItem;
    }
    const displayItem = buildDisplayItemFromSelectionRef(itemRef);
    const currentIndex = nextIndexByKind.get(displayItem.kind)
      ?? resolveMaxLabelIndexByKind(existing, displayItem.kind);
    const nextIndex = currentIndex + 1;
    nextIndexByKind.set(displayItem.kind, nextIndex);
    /**
     * SolidWorks 的选择列表编号跟随吸收历史，而不是每次按当前分组顺序重排。
     * 面圆角在面组间切换时尤其依赖这一点：面组2里的“面<2>”不能因为面组1新增面而漂移成“面<3>”。
     */
    return {
      ...displayItem,
      label: buildSelectionLabel(displayItem.kind, nextIndex),
    };
  });
}
